/* 🔴 F15 STEP 3, WORKOUT-BASED (his 2026-10-30 evening word, DECISIONS "THE ENGINE IS WORKOUT-BASED"): *"on/off for the
   engine and if on to just select how each will progress … workout based"*. The engine's settings live where the coach
   writes the WORKOUT, not on the library row.

   THE HOME (migration 20261030190000): `coach_workout_engine` (one row per workout: the switch) and `coach_workout_rules`
   (one row per (workout, library exercise): the rule this workout uses instead of the library's). Both are keyed on the
   ROOT workout — `coalesce(root_workout_id, id)` — and the library `exercise_id`, the two ids that survive copy-on-assign
   (`assign_coach_workout` stamps the root on every copy, every generation) and the builder's replace-not-merge save (it
   deletes sections and rows, never the `coach_workouts` row, and every rebuilt row carries the same `exercise_id`).
   MEASURED 2026-10-30: 107 of 109 client copies name a template root; the 2 without one are their own root.

   ONE MERGE, mirrored by `CoachWorkoutRules.effective` in Swift (the phone's next-week writer) over the same corpus
   (`__fixtures__/workoutRuleCorpus.json`): switch OFF → nothing progresses; a rule row → its config (NULL = this workout
   does not progress the row); no rule row → the library's `progression_config`. No new vocabulary: every config is one
   of the §5 shapes `ruleSentence.ts` already reads. */
import { lacksShort, ruleFacts, ruleSentenceBase, ruleShort } from "./ruleSentence";
import type { SetDraft } from "./builder";
import { isTabs, tabsJson, tabsRest } from "../../../supabase/functions/_shared/coachTabs";

/** The switch's default with no row: ON — today's behaviour (a row with a library rule progresses). */
export const PROGRESS_DEFAULT = true;

export type WorkoutEngine = {
  /** `coalesce(root_workout_id, id)` of the workout on screen — where both tables hang. */
  key: string;
  progress: boolean;
  /** Step 3.3: a deload every Nth week (`coach_workout_engine.deload_every`); `null` = never (no row, or the coach's "Never"). */
  deloadEvery: number | null;
  /** library exercise id → this workout's rule; a `null` value is "does not progress here". Absent = the library's. */
  rules: ReadonlyMap<string, unknown>;
};

/* 🔴 F15 STEP 3.3 — THE DELOAD CADENCE beside the switch: every Nth week (N − 1 loading weeks, then a deload in his own shape,
   `CoachDeload.swift`). One range, three readers: this list, the migration's CHECK (20261030210000) and `CoachDeload.cadences`
   — gate `f15-3-3-a-coached-week-deloads-and-sees-pain` r6 compares them. No default is chosen for the coach: blank = never. */
export const DELOAD_CHOICES = [3, 4, 5, 6, 7, 8] as const;

export function deloadLabel(every: number | null): string {
  return every === null ? "No deload" : `Deload every ${every}${every === 3 ? "rd" : "th"} week`;
}

export function engineKey(workout: { id: string; rootWorkoutId: string | null }): string {
  return workout.rootWorkoutId ?? workout.id;
}

export type RuleSource = "off" | "workout" | "library";

/** THE merge. `config` null = the row repeats unchanged next week. Step 3.4: an exercise a ladder of this workout moved the
 *  row to reads the REST of that chain (empty = the chain's end) — the position is derived, never stored. */
export function effectiveRule(engine: Pick<WorkoutEngine, "progress" | "rules">, exerciseId: string,
                              libraryConfig: unknown): { config: unknown; source: RuleSource } {
  if (!engine.progress) return { config: null, source: "off" };
  if (engine.rules.has(exerciseId)) return { config: engine.rules.get(exerciseId) ?? null, source: "workout" };
  const moved = movedRest(engine.rules, exerciseId);
  if (moved !== null) return { config: moved, source: "workout" };
  return { config: libraryConfig ?? null, source: "library" };
}

/** F15 step 4.1 — the rest of the first ladder OR tabs (ONE sorted, lower-cased key order, `CoachWorkoutRules.effective`'s
 *  `chains`) that moves a row to `exerciseId`, as JSON; null when none does. */
export function movedRest(rules: ReadonlyMap<string, unknown>, exerciseId: string): unknown {
  const id = exerciseId.toLowerCase();
  const chains = [...rules.entries()].filter(([, c]) => isLadder(c) || isTabs(c))
    .map(([k, c]) => [k.toLowerCase(), c] as const).sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  for (const [, config] of chains) {
    if (isLadder(config)) {
      const chain = parseLadder(config)!;
      const at = chain.links.findIndex((l) => l.next === id);
      if (at >= 0) return ladderJson(chain.links.slice(at + 1));
    } else {
      const rest = tabsRest(config, id);
      if (rest !== null) return tabsJson(rest);
    }
  }
  return null;
}

/* 🔴 F15 STEP 3.4 — HIS LADDER RULE (10-30 evening; the `docs/QUEUE.md` F15 line): on a row the coach picks the NEXT exercise
   and a target in the row's own units; links chain as long as the coach wants. Link i reads "when THIS exercise reaches the
   target, the row becomes `next`". The Swift twin is `CoachLadder.swift` (the phone performs the move); both read the
   corpus's `ladder.shapes` and the merge cases above. Ids are compared lower-case (the phone prints UUIDs upper-case). */
export const LADDER_RULE = "ladder";

export type LadderLink = { next: string; reps: number | null; kg: number | null; seconds: number | null; sessions: 1 | 2 };
export type LadderChain = { links: LadderLink[]; brokenAt: number | null };

const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);

export function isLadder(config: unknown): boolean { return isObj(config) && config.rule === LADDER_RULE; }

function readLink(item: unknown): LadderLink | null {
  if (!isObj(item) || typeof item.next !== "string" || item.next === "") return null;
  const present = (k: string) => k in item && item[k] !== null;
  const whole = (k: string) => (typeof item[k] === "number" && Number.isInteger(item[k]) ? (item[k] as number) : null);
  const reps = whole("reps"), seconds = whole("seconds");
  const kg = typeof item.kg === "number" ? item.kg : null;
  if (present("reps") && (reps ?? 0) < 1) return null;
  if (present("kg") && (kg ?? 0) <= 0) return null;
  if (present("seconds") && (seconds ?? 0) < 1) return null;
  if (reps === null && kg === null && seconds === null) return null;
  let sessions: 1 | 2 = 1;
  if (present("sessions")) {
    if (item.sessions !== 1 && item.sessions !== 2) return null;
    sessions = item.sessions;
  }
  return { next: item.next.toLowerCase(), reps, kg, seconds, sessions };
}

/** The chain a config states, `CoachLadder.chain`'s twin; null when it is not a ladder. A bad link ENDS the chain there. */
export function parseLadder(config: unknown): LadderChain | null {
  if (!isLadder(config)) return null;
  const raw = (config as Record<string, unknown>).links;
  if (!Array.isArray(raw)) return { links: [], brokenAt: 0 };
  const links: LadderLink[] = [];
  for (let i = 0; i < raw.length; i++) {
    const link = readLink(raw[i]);
    if (link === null) return { links, brokenAt: i };
    links.push(link);
  }
  return { links, brokenAt: null };
}

/** The links as stored JSON — absent targets stay absent (`CoachLadder.json`). */
export function ladderJson(links: readonly LadderLink[]): { rule: string; links: Record<string, unknown>[] } {
  return {
    rule: LADDER_RULE,
    links: links.map((l) => ({
      next: l.next, sessions: l.sessions,
      ...(l.reps !== null ? { reps: l.reps } : {}), ...(l.kg !== null ? { kg: l.kg } : {}),
      ...(l.seconds !== null ? { seconds: l.seconds } : {}),
    })),
  };
}

/** The rest of the first chain (key order, lower-cased) that names `exerciseId` as a next step; null when none does. */
export function ladderRest(rules: ReadonlyMap<string, unknown>, exerciseId: string): LadderLink[] | null {
  const id = exerciseId.toLowerCase();
  const ladders = [...rules.entries()].filter(([, c]) => isLadder(c))
    .map(([k, c]) => [k.toLowerCase(), c] as const).sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  for (const [, config] of ladders) {
    const chain = parseLadder(config)!;
    const at = chain.links.findIndex((l) => l.next === id);
    if (at >= 0) return chain.links.slice(at + 1);
  }
  return null;
}

function kgText(v: number): string { return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); }

/** "10 reps", "8 reps @ 20 kg", "30 s", "12.5 kg" (+ " twice running") — `CoachLadder.target`'s words. */
export function ladderTarget(link: Pick<LadderLink, "reps" | "kg" | "seconds" | "sessions">): string {
  const amount = [link.reps !== null ? `${String(link.reps)} reps` : null, link.seconds !== null ? `${String(link.seconds)} s` : null]
    .filter((x): x is string => x !== null);
  const load = link.kg !== null ? `${kgText(link.kg)} kg` : null;
  const text = amount.length === 0 ? (load ?? "") : amount.join(", ") + (load !== null ? ` @ ${load}` : "");
  return text + (link.sessions === 2 ? " twice running" : "");
}

/** The row's line for a ladder: "→ Weighted Pull-up at 10 reps, → One-arm Row at 12 reps". `names` maps a library id
 *  (lower-case) to its name; a missing one was deleted, and the chain stops there. */
export function ladderLine(config: unknown, names: ReadonlyMap<string, string>): { text: string; lacks: string | null } {
  const chain = parseLadder(config) ?? { links: [], brokenAt: 0 };
  const unreadable = "a step can't be read, the ladder stops there";
  if (chain.links.length === 0) {
    return chain.brokenAt === null ? { text: "Last step — stays as written", lacks: null } : { text: "Stays as written", lacks: unreadable };
  }
  const parts: string[] = [];
  let lacks: string | null = chain.brokenAt !== null ? unreadable : null;
  for (const link of chain.links) {
    const name = names.get(link.next);
    if (name === undefined) { parts.push("→ a deleted exercise"); lacks = "a next exercise was deleted, the ladder stops there"; break; }
    parts.push(`→ ${name} at ${ladderTarget(link)}`);
  }
  return { text: parts.join(", "), lacks };
}

/** The row's one line: what will happen next week, in the words `ruleSentence` already uses (a ladder: `ladderLine`). */
export function rowRuleLine(eff: { config: unknown; source: RuleSource },
                            names: ReadonlyMap<string, string> = new Map()): { text: string; lacks: string | null } {
  if (isLadder(eff.config)) return ladderLine(eff.config, names);
  const facts = ruleFacts(eff.config);
  if (facts === null) return { text: "Stays as written", lacks: null };
  return {
    text: ruleSentenceBase(facts.kind),
    lacks: facts.lacks.length === 0 ? null : facts.lacks.map((l) => l.words).join("; "),
  };
}

/* 🔴 F15 STEP 3.5 — THE ROW'S SHORT LINE (Fable, 10-30 night): one line at 1440, a coach's words; the long line above is the
   pop-up's second line. "Not progressing" is the row that repeats as written. A ladder's rest reads its NEXT step only. */
export const NOT_PROGRESSING = "Not progressing";

/** Round 2 (Fable): ONE toggle chip per link, off = once — the chip AND the short line say these words, and only when it is on. */
export const TWICE = "twice in a row";

/** "8 reps · 25 kg", "10 reps", "30 s", "25 kg" — a target as a coach writes it on a card, its parts in the units' order. */
export function targetShort(link: Pick<LadderLink, "reps" | "kg" | "seconds">): string {
  return [link.reps !== null ? `${String(link.reps)} reps` : null, link.seconds !== null ? `${String(link.seconds)} s` : null,
          link.kg !== null ? `${kgText(link.kg)} kg` : null].filter((x): x is string => x !== null).join(" · ");
}

/** A ladder's short line: its next step, then how many follow ("Next: Weighted Pull-up at 8 reps · 25 kg · +1 more"); the
 *  chain's end, a deleted or an unreadable step said in a word, the whole chain in `ladderLine` and in the editor. */
export function ladderShort(config: unknown, names: ReadonlyMap<string, string>): { text: string; lacks: string | null } {
  const chain = parseLadder(config) ?? { links: [], brokenAt: 0 };
  const first = chain.links[0];
  if (first === undefined) return { text: chain.brokenAt === null ? "Last step" : NOT_PROGRESSING, lacks: chain.brokenAt === null ? null : "a step can't be read" };
  const name = names.get(first.next);
  if (name === undefined) return { text: "Next: a deleted exercise", lacks: "the ladder stops" };
  const twice = first.sessions === 2 ? ` · ${TWICE}` : "";
  /* "+1 more" never comes apart at a wrap (a no-break space), so the line's chevron stays with both words. */
  const more = chain.links.length > 1 ? ` · +${String(chain.links.length - 1)}\u00a0more` : "";
  return { text: `Next: ${name} at ${targetShort(first)}${twice}${more}`, lacks: null };
}

/** The OWNING row's collapsed line (round 2): its chain's short line, or the ask when it has no link yet. */
export function chainSummary(config: unknown, names: ReadonlyMap<string, string>): { text: string; lacks: string | null } {
  const chain = parseLadder(config);
  if (chain !== null && chain.links.length === 0 && chain.brokenAt === null) return { text: `${CHAIN_LABEL} — add one`, lacks: null };
  return ladderShort(config, names);
}

/** The row's short line — `rowRuleLine`'s words cut to what a coach reads at a glance. */
export function rowRuleShort(eff: { config: unknown; source: RuleSource },
                             names: ReadonlyMap<string, string> = new Map()): { text: string; lacks: string | null } {
  if (isLadder(eff.config)) return ladderShort(eff.config, names);
  const facts = ruleFacts(eff.config);
  if (facts === null) return { text: NOT_PROGRESSING, lacks: null };
  return { text: ruleShort(facts.kind), lacks: lacksShort(facts) };
}

export type RuleChoice = {
  /** "library" removes this workout's row; every other choice writes `config` (null = does not progress). */
  key: "library" | "none" | "barbell" | "biweekly" | "ladder-kg" | "ladder" | "chain" | "steps";
  config: unknown;
};

/** The reps a working set asks for — warm-up sets are not the dose. */
function workingReps(sets: readonly SetDraft[]): number[] {
  return sets.filter((s) => s.type !== "warm_up" && s.reps !== null && s.reps > 0).map((s) => s.reps as number);
}

/** The rep ladder's top: +2 reps at 6 or fewer, +4 above (his own library's ladders: 3→5, 4→6, 6→8; 8→12, 8,8→12). */
export function ladderTop(start: readonly number[]): number {
  const low = Math.min(...start);
  return low + (low <= 6 ? 2 : 4);
}

/**
 * The choices a row offers — the §5 shapes with their own defaults and the row's own dose, never a number typed here.
 * A hold or timed row (no working reps) offers the library's and "does not progress" only.
 */
export function ruleChoices(sets: readonly SetDraft[]): RuleChoice[] {
  const out: RuleChoice[] = [{ key: "library", config: undefined }, { key: "none", config: null }];
  /* Step 3.4 — his ladder, on every row (a hold climbs to a harder hold): starts with no link; the editor adds them. */
  const chain: RuleChoice = { key: "chain", config: { rule: LADDER_RULE, links: [] } };
  const start = workingReps(sets);
  if (start.length === 0) return [...out, chain];
  const loaded = sets.some((s) => s.type !== "warm_up" && s.weightKg !== null && s.weightKg > 0);
  if (loaded) {
    out.push({ key: "barbell", config: { rule: "barbell_linear", step_kg: 2.5, hold_weeks: 2, min_rir: 2 } });
    out.push({ key: "biweekly", config: { rule: "weighted_bw_biweekly", step_kg: 2.5, every_weeks: 2 } });
  }
  const top = ladderTop(start);
  out.push({ key: "ladder-kg", config: { rule: "rep_ladder", start_reps: start, switch_reps: top, switch_step_kg: 2.5 } });
  out.push({ key: "ladder", config: { rule: "rep_ladder", start_reps: start, switch_reps: top, switch_step_kg: 0 } });
  out.push(chain);
  return out;
}

/** The menu's FIRST line for a choice — the row's own short words, so the chosen item reads as the row does. The library's
 *  entry is what the library's rule does; where it comes from is its second line, never a "Library:" prefix. */
export function choiceShort(choice: RuleChoice, libraryConfig: unknown): string {
  if (choice.key === "chain") return CHAIN_LABEL;
  return rowRuleShort({ config: choice.key === "library" ? libraryConfig : choice.config, source: "workout" }).text;
}

/** The menu's SECOND line: the full rule (the long sentence, its lacks after a dash), or where it comes from. */
export function choiceDetail(choice: RuleChoice, libraryConfig: unknown): string {
  if (choice.key === "chain") return "Moves to another exercise once a target is reached";
  const config = choice.key === "library" ? libraryConfig : choice.config;
  const facts = ruleFacts(config);
  const long = facts === null ? null
    : facts.lacks.length === 0 ? ruleSentenceBase(facts.kind) : `${ruleSentenceBase(facts.kind)} — ${facts.lacks.map((l) => l.words).join("; ")}`;
  if (choice.key === "library") return long === null ? "As in the library: repeats as written" : `As in the library: ${long}`;
  return long ?? "Repeats as written every week";
}

/** The menu's word for his ladder. */
export const CHAIN_LABEL = "Next exercise at a target";

export type LadderUnit = "reps" | "kg" | "seconds";

/** The target fields a link offers: the units the exercise it is MEASURED ON tracks (`coach_exercises.tracking_fields`) —
 *  reps, kilos, seconds. Nothing tracked: seconds for a hold, reps otherwise. */
export function ladderUnits(exercise: { trackingFields: readonly string[]; isHold: boolean } | undefined): LadderUnit[] {
  const t = exercise?.trackingFields ?? [];
  const out: LadderUnit[] = [];
  if (t.includes("reps")) out.push("reps");
  if (t.includes("time")) out.push("seconds");
  if (t.includes("weight")) out.push("kg");
  if (out.length === 0) out.push(exercise?.isHold === true ? "seconds" : "reps");
  return out;
}

/** A link the editor is still filling in: written only once it names an exercise AND a target (`readLink`'s bar). */
export type LinkDraft = { next: string | null; reps: number | null; kg: number | null; seconds: number | null; sessions: 1 | 2 };

export function linkComplete(d: LinkDraft): d is LinkDraft & { next: string } {
  return d.next !== null && (d.reps !== null || d.kg !== null || d.seconds !== null);
}

/** The exercises a link may move to: the coach's library (ours are in it as the starter rows), shown ones, never the row's
 *  own exercise or one already in the chain (a chain that loops is never offered). Sorted by name. */
export function ladderPicks<T extends { id: string; name: string; hiddenAt?: string | null }>(
  library: readonly T[], rowExerciseId: string, chain: readonly { next: string | null }[], keep: string | null,
): T[] {
  const taken = new Set([rowExerciseId.toLowerCase(), ...chain.map((l) => l.next).filter((x): x is string => x !== null)]);
  return library.filter((e) => (e.hiddenAt ?? null) === null && (!taken.has(e.id.toLowerCase()) || e.id.toLowerCase() === keep))
    .sort((a, b) => a.name.localeCompare(b.name));
}
